import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetMicrostoreColumnsPaginatedQuery,
    type MicrostoreColumnsFilterInput,
    MicrostoreElementState,
    MicrostoreColumnTemplateType,
    MicrostoreColumnType,
    ExternalColumnType,
} from 'data/__generated__';
import {
    textColumn,
    booleanColumn,
    multiSelectColumn,
    dateColumn,
    numberColumn,
    entityColumn,
} from 'components/DataTable';
import { siteSource } from 'data/entity-sources/sources';
import { DetailFormStatusValue } from 'components/DetailsView';
import { MICROSTORE_COLUMN_STATES_OPTIONS } from './states';
import { MICROSTORE_COLUMN_TEMPLATE_TYPES_OPTIONS } from './template-types';
import { MICROSTORE_COLUMN_EXTERNAL_TYPES_OPTIONS } from './external-column-types';

export type ColumnRow = GetMicrostoreColumnsPaginatedQuery['microstoreColumnsFilter']['microstoreColumns'][number];

export const columnsListColumns: ColumnDef<ColumnRow, unknown>[] = [
    dateColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date de création',
    }),
    dateColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'updatedAt',
        header: 'Date de mise à jour',
    }),
    textColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'name',
        header: 'Nom',
        defaultVisible: true,
        filterKey: 'name',
    }),
    // La colonne montre le NOM du site, le filtre porte sur des IDENTIFIANTS (`siteIds`) : plus de
    // recherche textuelle approximative, et un site homonyme ne ramène plus les colonnes de l'autre.
    // Rien d'autre à écrire ici — `siteSource` porte la recherche serveur et la résolution des
    // libellés d'une sélection restaurée depuis l'URL.
    entityColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'siteName',
        header: 'Site',
        defaultVisible: true,
        filterKey: 'siteIds',
        source: siteSource,
    }),
    textColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        id: 'microstoreColumnTemplateName',
        accessorFn: (row) => row.microstoreColumnTemplateName || '',
        header: 'Template',
        defaultVisible: true,
        filterKey: 'microstoreColumnTemplateName',
    }),
    multiSelectColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        defaultVisible: true,
        filterKey: 'states',
        options: Object.values(MicrostoreElementState).map((state) => ({ value: state, label: state })),
        renderOption: (option) => (
            <DetailFormStatusValue
                state={option.value as MicrostoreElementState}
                options={MICROSTORE_COLUMN_STATES_OPTIONS}
            />
        ),
        cell: ({ row }) => (
            <DetailFormStatusValue state={row.original.state} options={MICROSTORE_COLUMN_STATES_OPTIONS} />
        ),
    }),
    multiSelectColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'type',
        header: 'Type',
        defaultVisible: true,
        filterKey: 'types',
        options: Object.values(MicrostoreColumnTemplateType).map((type) => ({ value: type, label: type })),
        renderOption: (option) => (
            <DetailFormStatusValue
                state={option.value as MicrostoreColumnTemplateType}
                options={MICROSTORE_COLUMN_TEMPLATE_TYPES_OPTIONS}
            />
        ),
        cell: ({ row }) =>
            row.original.type ? (
                <DetailFormStatusValue state={row.original.type} options={MICROSTORE_COLUMN_TEMPLATE_TYPES_OPTIONS} />
            ) : null,
    }),
    booleanColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        id: 'isUnsecured',
        accessorFn: (row) => row.microstoreColumnType === MicrostoreColumnType.UnsecuredMicrostore,
        header: 'Unsecured',
        defaultVisible: true,
    }),
    multiSelectColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'externalColumnType',
        header: 'Type de télémétrie',
        defaultVisible: true,
        filterKey: 'externalColumnTypes',
        options: Object.values(ExternalColumnType).map((type) => ({ value: type, label: type })),
        renderOption: (option) => (
            <DetailFormStatusValue
                state={option.value as ExternalColumnType}
                options={MICROSTORE_COLUMN_EXTERNAL_TYPES_OPTIONS}
            />
        ),
        cell: ({ row }) => (
            <DetailFormStatusValue
                state={row.original.externalColumnType}
                options={MICROSTORE_COLUMN_EXTERNAL_TYPES_OPTIONS}
            />
        ),
    }),
    booleanColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'isPaused',
        header: 'En pause',
    }),
    textColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        id: 'externalColumnId',
        accessorFn: (row) => row.externalColumnId || '',
        header: 'ID Télémétrie',
        filterKey: 'externalColumnId',
    }),
    numberColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        id: 'avaSalePlaceId',
        accessorFn: (row) => row.avaSalePlaceId ?? '',
        header: 'Ava Machine Id',
        filterKey: 'avaSalePlaceId',
    }),
    dateColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        id: 'startingDate',
        accessorFn: (row) => row.startingDate || '',
        header: 'Date de début',
    }),
    booleanColumn<ColumnRow, MicrostoreColumnsFilterInput>({
        accessorKey: 'isRegular',
        header: 'Régulier',
        defaultVisible: true,
        filterKey: 'isRegular',
    }),
];
